// One Turnstile implementation for this app's auth surfaces.
//
// WHY A SECOND COPY EXISTS, and why it is not optional: this app signs people in against the SAME
// Supabase project as TrackingMCP (apps/web/src/lib/supabase.ts). Supabase's "Enable CAPTCHA
// protection" is a PROJECT-WIDE switch — the moment it is turned on, GoTrue demands a token on every
// sign-up, password sign-in, magic link and password reset for every app on that project. An app that
// sends no token does not degrade: its sign-in stops working outright. TrackingMCP already sends one;
// this file is what lets that switch be flipped without taking this product's login down with it.
//
// THE RULE THIS FILE EXISTS TO KEEP: a legitimate person must never be asked to do anything. Turnstile
// runs in `interaction-only` appearance, which renders NOTHING for a visitor who passes passively —
// the overwhelming majority. A widget appears only for a session Cloudflare cannot clear on its own,
// which is the case we actually want to slow down. Every failure path (script blocked by an extension,
// network down, timeout, widget error) resolves to null rather than throwing, because a broken
// challenge must never be the reason a real customer cannot sign in.
//
// Kept deliberately identical to TrackingMCP's copy (apps/web/src/lib/turnstile.ts) — including the
// `?render=explicit` in the script URL, whose absence cost twelve hours of locked-out sign-ins on
// 2026-09-17. Fix a bug here, fix it there.

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string
      remove: (id: string) => void
    }
  }
}

/**
 * The site key for the auth surfaces (sign-up, sign-in, password reset).
 *
 * Empty until it is set, and EVERY caller here treats empty as "no challenge". That is what makes the
 * rollout safe in either order: shipping this code with no key configured changes nothing, and the
 * Supabase dashboard switch can be flipped afterwards. The tracking surface gets its key from the API
 * at challenge time instead, because there the decision to challenge is the server's.
 */
export const AUTH_TURNSTILE_SITEKEY =
  (import.meta.env.VITE_TURNSTILE_SITEKEY as string | undefined) ?? ''

// How long a challenge may take before we stop waiting.
//
// It was 8s until 2026-09-23, on the reasoning that a real widget answers fast. It does not always.
// Measured against the production widget on trackingmcp.com and app.trackingmcp.com that day: the
// widget sat on "Verifying…" for 9s and 14s in two runs before it said anything at all. Eight seconds
// did not time out a broken challenge — it cut off a live one, handed the caller null, and GoTrue then
// refused the sign-in with "no captcha_token found". Roughly eight distinct customer addresses hit
// that in a day and never once got in; two of them reached support.
//
// The number a deadline has to beat is how long a SLOW SUCCESS takes, not how long a person is willing
// to wait — because the alternative to waiting is not a faster sign-in, it is a refused one. Thirty
// seconds is past everything observed, and the button says what it is doing while it waits.
const CHALLENGE_TIMEOUT_MS = 30_000

let turnstileScript: Promise<void> | null = null

/** Load Cloudflare's script once, on first use. Never loaded for a visitor who is never challenged. */
function loadTurnstile(): Promise<void> {
  turnstileScript ??= new Promise((resolve, reject) => {
    const el = document.createElement('script')
    // The path carries a version — /turnstile/v0/api.js. Without it Cloudflare answers 404, the script
    // never loads, and every challenge silently resolves to "no token". That is not a hypothesis: it is
    // what this line did from the day it was written until 2026-09-17, on every surface in the family,
    // and it is why enabling captcha protection locked every password sign-in out for twelve hours.
    el.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    el.async = true
    el.onload = () => resolve()
    el.onerror = () => reject(new Error('turnstile blocked'))
    document.head.appendChild(el)
  })
  return turnstileScript
}

/**
 * Run an invisible Turnstile challenge and resolve its token, or null.
 *
 * Null on anything going wrong — blocked script, expired widget, timeout, no key. The caller decides
 * what that means; for auth it means "send no token and let the server judge", never "refuse to try".
 */
/**
 * Set once the widget has proved it cannot run here — a bad sitekey, or a hostname the widget was
 * never configured for.
 *
 * Without this, EVERY sign-in attempt pays the full challenge deadline before failing the same way it
 * failed a moment ago. On 2026-09-17 that was eight seconds added to every attempt of an already
 * hopeless sign-in, on top of the refusal itself. The first attempt still pays it, because the first
 * attempt is how we learn; the rest should not.
 *
 * WHAT MAY SET IT, and what may not (2026-09-23): only a Cloudflare 110xxx code, which is the family
 * that means the WIDGET is wrong — unknown sitekey, unknown domain, bad configuration. Nothing else.
 * It used to be set by the deadline as well, on the theory that silence means a widget that cannot run
 * here. Silence also means a challenge still working, and a customer whose first attempt was merely
 * slow was then told "no" instantly for the rest of the page's life, with no way to tell the
 * difference and nothing on screen suggesting a reload. A slow challenge is not a broken one.
 *
 * Per page load, deliberately. A reload is a fair second chance, and a fix on our side should reach a
 * waiting customer as soon as they retry rather than whenever a cache decides.
 */
let widgetIsUnusable = false

/**
 * Cloudflare's 110xxx client error codes: the sitekey or the domain is wrong, i.e. OUR configuration.
 * Every other code (600xxx, 300xxx, …) is an execution failure that a retry can legitimately clear.
 */
function isWidgetMisconfigured(code: string): boolean {
  return /^110\d{3}$/.test(code)
}

/** Test seam: a page that never reloads during a test run would otherwise keep the flag forever. */
export function resetTurnstileHealth(): void {
  widgetIsUnusable = false
}

export async function solveTurnstile(
  sitekey: string,
  timeoutMs = CHALLENGE_TIMEOUT_MS,
): Promise<string | null> {
  if (!sitekey) return null
  if (widgetIsUnusable) return null
  try {
    await loadTurnstile()
    if (!window.turnstile) return null
    return await new Promise<string | null>((resolve) => {
      // WHERE THE WIDGET GOES, and why it moved on 2026-09-23. `interaction-only` draws nothing for a
      // visitor who passes passively, so for almost everyone this element stays empty and invisible
      // wherever it sits. The exception is the whole point of it: the visitor Cloudflare cannot clear
      // on its own gets a checkbox to tick — and that checkbox used to appear 16px into the bottom
      // right corner, away from the form the person is looking at, for as long as the deadline lasted.
      // A control nobody notices is a control nobody ticks. Bottom centre, above everything.
      const host = document.createElement('div')
      host.style.position = 'fixed'
      host.style.bottom = '24px'
      host.style.left = '50%'
      host.style.transform = 'translateX(-50%)'
      host.style.zIndex = '9999'
      document.body.appendChild(host)
      let settled = false
      const done = (t: string | null) => {
        if (settled) return
        settled = true
        clearTimeout(bail)
        try {
          if (id) window.turnstile?.remove(id)
        } catch {
          /* already gone */
        }
        host.remove()
        resolve(t)
      }
      // The hard stop. Turnstile has its own timeout-callback, but it only fires when the widget is
      // alive enough to fire it — a script that loads and then hangs, or a network that swallows the
      // challenge mid-flight, calls nothing at all and this promise would never settle. Sitting inside
      // an awaited call on the sign-in path, that is not a failed challenge: it is a login button that
      // spins forever. Give up, hand back null, and let the server decide.
      const bail = setTimeout(() => {
        // Nothing came back in thirty seconds. That is a dead challenge, but it is NOT evidence the
        // widget is misconfigured — see widgetIsUnusable — so the next attempt is allowed to try again.
        done(null)
      }, timeoutMs)
      const id = window.turnstile?.render(host, {
        sitekey,
        // Renders nothing unless Cloudflare genuinely needs the visitor to interact.
        appearance: 'interaction-only',
        // Let the widget retry itself on a transient failure instead of the caller eating the first
        // one. 600xxx is an execution failure, not a verdict: on 2026-09-23 the production widget
        // returned 600010 after 9–14s of "Verifying…" on a run that had nothing wrong with it. The
        // deadline above still bounds the whole thing, so a retry loop cannot outlive the sign-in.
        retry: 'auto',
        'retry-interval': 2_000,
        'refresh-expired': 'auto',
        callback: (token: string) => done(token),
        // Returning true tells Turnstile to retry; returning false ends the widget. Only a
        // configuration error ends it, because only a configuration error cannot come good on a retry.
        'error-callback': (code?: unknown) => {
          if (isWidgetMisconfigured(String(code ?? ''))) {
            widgetIsUnusable = true
            done(null)
            return false
          }
          return true
        },
        'timeout-callback': () => done(null),
      })
      if (!id) done(null)
    })
  } catch {
    return null
  }
}

/**
 * A captcha token for a Supabase auth call, in the shape supabase-js expects.
 *
 * Returns `undefined` — not null, not an empty string — when there is no key or the challenge did not
 * produce one, so the result can be spread straight into `options` and simply not be there:
 *
 *     await supabase.auth.signUp({ email, password, options: { ...(await authCaptchaOptions()) } })
 *
 * GoTrue is the enforcer, not this function. With captcha switched off in the project it ignores the
 * field; with captcha switched on it rejects a request that arrives without a valid one — including
 * requests that never touched our forms, which is the entire point. The attack hit /auth/v1/signup and
 * /auth/v1/token directly; a check living in our React code would not have been in its path at all.
 */
export async function authCaptchaOptions(): Promise<{ captchaToken?: string }> {
  if (!AUTH_TURNSTILE_SITEKEY) return {}
  const token = await (takeWarmChallenge() ?? solveTurnstile(AUTH_TURNSTILE_SITEKEY))
  return token ? { captchaToken: token } : {}
}

// ── Starting the challenge before the person needs it ────────────────────────
//
// The deadline above has to beat a SLOW SUCCESS — 9s and 14s were measured on the production widget on
// 2026-09-23 — which is why it is thirty seconds. That is the right number for "how long may a
// challenge take", and the wrong thing to make a human watch: pressed at submit, it is thirty seconds
// of a spinning sign-in button for anyone whose network is slow to reach Cloudflare.
//
// Both facts hold at once if the challenge starts when the page OPENS rather than when the button is
// pressed. A person spends ten to thirty seconds typing an address and a password; the widget spends
// that same time working, and by the click the token is usually already in hand. Nobody waits, and a
// slow challenge still gets its full budget.
//
// A token is single-use and Cloudflare expires it after five minutes, so a warmed one is taken exactly
// once and is not trusted after four — past that the submit solves a fresh one and pays the wait it
// was always going to pay.
const WARM_TOKEN_TTL_MS = 4 * 60 * 1000
let warmChallenge: Promise<string | null> | null = null
let warmStartedAt = 0

/**
 * Begin an auth challenge now, in the background.
 *
 * Call it when an auth page mounts. Safe to call repeatedly: a challenge already in flight (or a fresh
 * token already held) is left alone, so a re-render does not start a second widget.
 */
export function prewarmAuthChallenge(): void {
  if (!AUTH_TURNSTILE_SITEKEY) return
  if (warmChallenge && Date.now() - warmStartedAt < WARM_TOKEN_TTL_MS) return
  warmStartedAt = Date.now()
  // Never rejects — solveTurnstile resolves null on every failure path — but the catch keeps an
  // unhandled rejection impossible if that ever changes.
  warmChallenge = solveTurnstile(AUTH_TURNSTILE_SITEKEY).catch(() => null)
}

/** The warmed challenge, if one is in flight and still young. Consumes it: a token is single-use. */
function takeWarmChallenge(): Promise<string | null> | null {
  if (!warmChallenge) return null
  const fresh = Date.now() - warmStartedAt < WARM_TOKEN_TTL_MS
  const pending = warmChallenge
  warmChallenge = null
  return fresh ? pending : null
}

/** Test seam: forget any warmed challenge between cases. */
export function resetWarmChallenge(): void {
  warmChallenge = null
  warmStartedAt = 0
}

/**
 * Turn a GoTrue captcha rejection into a sentence the person can act on.
 *
 * THE DISRUPTION THIS EXISTS TO PREVENT: a real customer running a strict ad blocker, a privacy
 * extension, or a corporate network that blocks challenges.cloudflare.com never gets a token. Their
 * request reaches GoTrue without one and comes back as "captcha protection: request disallowed" — a
 * sentence that tells them nothing and looks like our service is broken.
 *
 * Note what this deliberately does NOT do: it does not refuse to make the call when the challenge
 * fails. If the project setting happens to be off, a tokenless request succeeds and the person signs
 * in none the wiser. We only translate a refusal that has actually happened — we never manufacture one
 * on the client, because the client cannot know whether a token was required.
 *
 * Returns null for any other error, so ordinary failures keep their own wording.
 */
export function captchaErrorMessage(err: { message?: string } | null | undefined): string | null {
  const m = String(err?.message ?? '')
  if (!/captcha/i.test(m)) return null
  // WHAT THIS USED TO SAY, and why it was changed on 2026-09-17: "An ad blocker or privacy extension is
  // the usual cause — pause it for this page, or try a different browser." That sentence was written on
  // the assumption that a failing challenge is the visitor's environment. It was not. Our Turnstile
  // widget had never been allowed to run on the site's own hostname, so it produced no token for
  // ANYONE, and four customers spent their morning disabling extensions and switching browsers to fix
  // a misconfiguration on our side.
  //
  // Never send someone to go and change their own setup for a fault we cannot rule out as ours. Say
  // what happened, give them a route that works right now, and make the failure ours to answer for.
  return 'The security check could not be completed. This is usually a fault on our side, not yours. Signing in with Google or GitHub works right now, and support@trackingmcp.com will get you in if you are stuck.'
}
